/**
 * Defaults y presets de configuración de reservas (turnos, días, mensajes).
 */
import { normalizeRestaurantWa } from "./bookingUtils";

export const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const emptyDaysOfWeek = () =>
  DAY_KEYS.reduce((acc, k) => {
    acc[k] = { enabled: false, turns: [] };
    return acc;
  }, {});

/* ── Presets de turnos ── */
export const TURN_PRESETS = {
  none: {
    id: "none",
    labelKey: "reservations.config.presetNone",
    apply: () => emptyDaysOfWeek(),
  },
  weekendDinner: {
    id: "weekendDinner",
    labelKey: "reservations.config.presetWeekendDinner",
    apply: () => {
      const d = emptyDaysOfWeek();
      const dinner = [{ start: "20:00", end: "22:00" }, { start: "22:30", end: "00:30" }];
      d.friday = { enabled: true, turns: dinner };
      d.saturday = { enabled: true, turns: dinner };
      d.sunday = { enabled: true, turns: [{ start: "20:00", end: "22:30" }] };
      return d;
    },
  },
  dinnerEveryday: {
    id: "dinnerEveryday",
    labelKey: "reservations.config.presetDinnerEveryday",
    apply: () => {
      const d = emptyDaysOfWeek();
      const dinner = [{ start: "20:00", end: "22:00" }, { start: "22:30", end: "00:30" }];
      DAY_KEYS.forEach((k) => { d[k] = { enabled: true, turns: dinner }; });
      return d;
    },
  },
  lunchAndDinner: {
    id: "lunchAndDinner",
    labelKey: "reservations.config.presetLunchDinner",
    apply: () => {
      const d = emptyDaysOfWeek();
      const turns = [
        { start: "12:30", end: "14:30" },
        { start: "14:30", end: "16:00" },
        { start: "20:00", end: "22:00" },
        { start: "22:30", end: "00:30" },
      ];
      DAY_KEYS.forEach((k) => { d[k] = { enabled: true, turns }; });
      return d;
    },
  },
};

/* ── Plantillas de mensajes WA ── */
export const defaultMessages = (langCode = "es") => {
  if (langCode === "en") {
    return {
      confirmation: "Hi {name}! Your reservation for {date} at {time} for {people} people is confirmed. See you soon! If you can't make it, please cancel here: {manageLink}",
      rejection: "Hi {name}, sorry — we can't take your reservation for {date} at {time}. Would another time work for you?",
      changeOfTable: "Hi {name}! Your reservation for {date} at {time} is confirmed but I'm assigning you {tableName} (similar to the one you requested). Sound good?",
      reminder: "Hi {name}! A reminder of your reservation for {date} at {time} for {people}. See you soon!",
    };
  }
  if (langCode === "pt") {
    return {
      confirmation: "Olá {name}! Sua reserva para {date} às {time} para {people} pessoas está confirmada. Até logo! Se não puder vir, cancele aqui: {manageLink}",
      rejection: "Olá {name}, infelizmente não podemos atender sua reserva para {date} às {time}. Algum outro horário funcionaria?",
      changeOfTable: "Olá {name}! Sua reserva para {date} às {time} está confirmada, mas estou atribuindo {tableName} (similar à que pediu). Tudo bem?",
      reminder: "Olá {name}! Lembrete da sua reserva para {date} às {time} para {people}. Até logo!",
    };
  }
  return {
    confirmation: "¡Hola {name}! Te confirmo tu reserva para el {date} a las {time} para {people} personas. ¡Nos vemos! Si no podés venir, cancelá acá: {manageLink}",
    rejection: "Hola {name}, lamentablemente no podemos confirmar tu reserva para el {date} a las {time}. ¿Te sirve otro horario?",
    changeOfTable: "¡Hola {name}! Te confirmo tu reserva del {date} a las {time}, pero te asigno {tableName} (similar a la que pediste). ¿Te parece bien?",
    reminder: "¡Hola {name}! Te recuerdo tu reserva del {date} a las {time} para {people}. ¡Nos vemos!",
  };
};

/* spaceMode = gestión de ESPACIO (agenda/zones/tables) — headline del onboarding.
 * timeMode  = selección de HORA (free/turns) — ex "mode". Son ortogonales. */
export const emptyConfig = () => ({
  enabled: false,
  onboarded: false,
  spaceMode: "agenda",
  timeMode: "free",
  restaurantName: "",
  coverPhotoUrl: "", // foto del local — se muestra arriba en la página /book del cliente
  whatsappNumber: "",
  turnDurationMinutes: 120,
  slotIntervalMinutes: 15, // cada cuántos minutos se ofrece un horario para reservar en modo libre (15/30/60)
  minAdvanceMinutes: 0, // anticipación mínima para reservar hoy (0 = sin mínimo; igual oculta horas ya pasadas)
  tableTurnaroundMinutes: 15, // margen de limpieza/rotación entre dos reservas de la misma mesa (aviso, no bloqueo)
  maxAheadDays: 60,
  totalCapacity: 0, // 0 = sin tope; cupo de personas en simultáneo (lo usa checkCapacity en modo agenda)
  daysOfWeek: emptyDaysOfWeek(),
  blockedDates: [], // cierres puntuales (feriados que NO atendés, vacaciones)
  // Excepciones por fecha: ARRAY de { date:'YYYY-MM-DD', open: bool, turns?, timeMode? }.
  // open:true = abrir un feriado fuera del horario semanal; open:false = cerrar
  // puntual. Tiene prioridad sobre blockedDates y daysOfWeek (ver effectiveDayConfig).
  // Es array (no mapa) para que Firestore reemplace la lista entera al guardar
  // (con setDoc merge un mapa hace deep-merge y NO borra claves → los quites no
  // persistían). Igual criterio que blockedDates.
  dateOverrides: [],
  zones: [],
  messages: defaultMessages("es"),
  createdAt: Date.now(),
});

/* Normaliza un config crudo de Firestore a la forma canónica actual.
 * Compat: docs viejos sin spaceMode se tratan como "tables"; el viejo `mode`
 * se mapea a `timeMode`; si ya tenían whatsappNumber los damos por onboarded.
 * El whatsappNumber se auto-repara a E.164 AR (5491157463566) en cada lectura:
 * arregla en el acto los números viejos guardados sin código de país sin tocar la
 * base (al próximo guardado queda persistido bien). */
export const normalizeConfig = (raw) => {
  if (!raw) return emptyConfig();
  return {
    ...emptyConfig(),
    ...raw,
    spaceMode: raw.spaceMode || "tables",
    timeMode: raw.timeMode || raw.mode || "free",
    onboarded: raw.onboarded ?? !!raw.whatsappNumber,
    whatsappNumber: normalizeRestaurantWa(raw.whatsappNumber),
    dateOverrides: Array.isArray(raw.dateOverrides) ? raw.dateOverrides : [],
    zones: raw.zones || [],
    messages: { ...defaultMessages(raw.locale || "es"), ...(raw.messages || {}) },
  };
};

/* ── Time helpers ── */
export const parseTime = (str) => {
  const [h = 0, m = 0] = (str || "00:00").split(":").map(Number);
  return h * 60 + m;
};

export const isValidTimeStr = (str) => /^([01]?\d|2[0-3]):[0-5]\d$/.test(str || "");

export const formatDate = (d) => {
  if (typeof d === "string") return d;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
